import { useCallback, useEffect, useState } from 'react';

import NewCustomDataTable from '../common/NewCustomDataTable';
import { Skeleton, SkeletonGroup } from '../common/Skeleton/Skeleton.jsx';
import { toast } from '../common/Toast/toast';
import { silenceFor } from '../common/Loader/busy';
import {
  accessErrorMessage,
  cancelAccessRequest,
  createAccessRequest,
  getAccessOptions,
  getAccessRequests,
} from '../../../services/AccessRequestService';
import { StatusPill, formatWhen } from './accessStatus.jsx';
import '../Trainer/TrainerDashboard.css';
import './access-center.css';

const PAGE_SIZE = 10;

/**
 * Ask an administrator for another role.
 *
 * The form offers only what the API says this user's roles may request
 * (GET /accessrequests/options) -- and the API checks it again when the
 * request is filed, so the list is a convenience, not the gate. An approved
 * request adds the role to the user's account; they see it on their next
 * sign-in or reload.
 */
const AccessCenter = () => {
  const [options, setOptions] = useState({ currentRoles: [], requestable: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [roleId, setRoleId] = useState('');
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      // The panel shows its own skeleton, so the global overlay stays down.
      silenceFor();
      setOptions(await getAccessOptions());
    } catch (error) {
      toast.error(accessErrorMessage(error, 'Could not load what you can request'));
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async (nextPage) => {
    setLoading(true);
    try {
      const result = await getAccessRequests({ page: nextPage, limit: PAGE_SIZE });
      setRows(result.data);
      setTotal(result.pagination?.total || 0);
      setPage(nextPage);
    } catch (error) {
      toast.error(accessErrorMessage(error, 'Could not load your requests'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
    loadRequests(1);
  }, [loadOptions, loadRequests]);

  const submit = async (e) => {
    e.preventDefault();
    if (!roleId || sending) return;
    setSending(true);
    try {
      await createAccessRequest({ requestedRoleId: roleId, reason: reason.trim() });
      toast.success('Request sent to the administrators');
      setRoleId('');
      setReason('');
      loadOptions();
      loadRequests(1);
    } catch (error) {
      toast.error(accessErrorMessage(error, 'Could not send the request'));
    } finally {
      setSending(false);
    }
  };

  const withdraw = async (row) => {
    try {
      await cancelAccessRequest(row.id);
      toast.success('Request withdrawn');
      loadOptions();
      loadRequests(page);
    } catch (error) {
      toast.error(accessErrorMessage(error, 'Could not withdraw the request'));
    }
  };

  const { currentRoles = [], requestable = [] } = options;
  const askable = requestable.filter((r) => !r.pending);

  const headers = [
    { title: 'Role', prop: 'requestedRoleLabel', cell: (r) => r.requestedRoleLabel || '—' },
    { title: 'Asked on', prop: 'createdAt', cell: (r) => formatWhen(r.createdAt) },
    { title: 'Status', prop: 'status', cell: (r) => <StatusPill status={r.status} /> },
    { title: 'Note', prop: 'reviewNote', cell: (r) => r.reviewNote || '—' },
    {
      title: '',
      prop: 'action',
      cell: (r) =>
        r.status === 'pending' ? (
          <button type="button" className="pgm-btn" onClick={() => withdraw(r)}>
            Withdraw
          </button>
        ) : null,
    },
  ];

  return (
    <div className="dashboard-wrapper">
      <div className="td-page acc-page">
        <section className="td-panel">
          <div className="td-panel-head">
            <h2 className="td-panel-title">Request access</h2>
            <p className="td-panel-sub">
              Ask an administrator for another role. What you can ask for depends on the role you
              have now.
            </p>
          </div>

          {optionsLoading ? (
            <SkeletonGroup label="Loading what you can request" className="lms-skel-stack">
              <div className="lms-skel-row">
                <Skeleton shape="pill" />
                <Skeleton shape="pill" />
              </div>
              <div className="acc-form">
                <div className="pg-field lms-skel-stack">
                  <Skeleton width="25" />
                  <Skeleton shape="row" />
                </div>
                <div className="pg-field pg-field--grow lms-skel-stack">
                  <Skeleton width="25" />
                  <Skeleton shape="row" />
                </div>
              </div>
            </SkeletonGroup>
          ) : (
            <>
              <div className="acc-roles" aria-label="Your roles">
                {currentRoles.length ? (
                  currentRoles.map((r) => (
                    <span className="pgm-pill" key={r.id}>
                      {r.label}
                    </span>
                  ))
                ) : (
                  <span className="pgm-empty">No role is assigned to your account yet.</span>
                )}
              </div>

              {requestable.length === 0 ? (
                <p className="pgm-empty">
                  Your role has nothing it can request. An administrator decides what each role may
                  ask for.
                </p>
              ) : (
                <form className="acc-form" onSubmit={submit}>
                  <div className="pg-field">
                    <label className="pg-label" htmlFor="acc-role">
                      Role
                    </label>
                    <select
                      id="acc-role"
                      className="pg-select"
                      value={roleId}
                      onChange={(e) => setRoleId(e.target.value)}
                    >
                      <option value="">{askable.length ? 'Choose a role…' : 'All already requested'}</option>
                      {requestable.map((r) => (
                        <option key={r.id} value={r.id} disabled={r.pending}>
                          {r.pending ? `${r.label} — requested` : r.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-field pg-field--grow">
                    <label className="pg-label" htmlFor="acc-reason">
                      Why you need it
                    </label>
                    <textarea
                      id="acc-reason"
                      className="pg-input pg-textarea"
                      rows={3}
                      maxLength={1000}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Optional, but it helps the administrator decide."
                    />
                  </div>

                  <div className="acc-form-foot">
                    <button type="submit" className="pg-btn pg-btn--main" disabled={!roleId || sending}>
                      {sending ? 'Sending…' : 'Send request'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </section>

        <section className="td-panel">
          <div className="td-panel-head">
            <h2 className="td-panel-title">Your requests</h2>
          </div>
          <div className="td-list">
            <NewCustomDataTable
              data={rows}
              headers={headers}
              totalRecords={total}
              currentPage={page}
              recordsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={loadRequests}
              pageTitle="access requests"
              addButton={false}
              exportButton={false}
              showToolbar={false}
              serverSide
              loading={loading}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AccessCenter;
